import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HexColorPicker } from 'react-colorful';

const TypeSettings = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#6366F1');
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  useEffect(() => {
    fetchTypes();
  }, []);
  
  const fetchTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/types');
      
      if (response.data && response.data.types) {
        setTypes(response.data.types);
      } else {
        setTypes([]);
      }
    } catch (err) {
      console.error('Error fetching types:', err);
      setError('Failed to load types. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddType = async (e) => {
    e.preventDefault();
    
    if (!newTypeName.trim()) {
      toast.error('Type name cannot be empty');
      return;
    }
    
    try {
      const response = await api.post('/types', {
        name: newTypeName,
        color: newTypeColor
      });
      
      if (response.data && response.data.type) {
        setTypes([...types, response.data.type]);
        
        // Reset form
        setNewTypeName('');
        setNewTypeColor('#6366F1');
        setShowAddForm(false);
        
        toast.success('Type added successfully');
      }
    } catch (error) {
      console.error('Error adding type:', error);
      toast.error(error.response?.data?.error || 'Failed to add type');
    }
  };
  
  const handleEditType = async (e) => {
    e.preventDefault();
    
    if (!newTypeName.trim()) {
      toast.error('Type name cannot be empty');
      return;
    }
    
    try {
      const response = await api.put(`/types/${editingType.id}`, {
        name: newTypeName,
        color: newTypeColor
      });
      
      if (response.data && response.data.type) {
        // Update types list
        setTypes(
          types.map(type => 
            type.id === editingType.id ? response.data.type : type
          )
        );
        
        // Reset form
        setEditingType(null);
        setNewTypeName('');
        setNewTypeColor('#6366F1');
        setShowEditForm(false);
        
        toast.success('Type updated successfully');
      }
    } catch (error) {
      console.error('Error updating type:', error);
      toast.error(error.response?.data?.error || 'Failed to update type');
    }
  };
  
  const handleDeleteType = async (typeId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this type? Projects with this type will be moved to the Default type.');
    
    if (!confirmDelete) return;
    
    try {
      await api.delete(`/types/${typeId}`);
      
      // Remove from types list
      setTypes(types.filter(type => type.id !== typeId));
      
      toast.success('Type deleted successfully');
    } catch (error) {
      console.error('Error deleting type:', error);
      toast.error(error.response?.data?.error || 'Failed to delete type');
    }
  };
  
  const handleEditClick = (type) => {
    setEditingType(type);
    setNewTypeName(type.name);
    setNewTypeColor(type.color);
    setShowEditForm(true);
    setShowAddForm(false);
  };
  
  const handleAddClick = () => {
    setShowAddForm(true);
    setShowEditForm(false);
    setNewTypeName('');
    setNewTypeColor('#6366F1');
  };
  
  if (loading && types.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading types...</p>
      </div>
    );
  }
  
  if (error && types.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchTypes}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Types</h2>
        <button
          type="button"
          onClick={handleAddClick}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Type
        </button>
      </div>
      
      {/* Add/Edit Form */}
      {(showAddForm || showEditForm) && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h3 className="text-base font-medium text-gray-900 mb-3">
            {showAddForm ? 'Add New Type' : 'Edit Type'}
          </h3>
          <form onSubmit={showAddForm ? handleAddType : handleEditType}>
            <div className="flex space-x-4">
              <div className="flex-grow">
                <label htmlFor="typeName" className="block text-sm font-medium text-gray-700 mb-1">
                  Type Name
                </label>
                <input
                  type="text"
                  id="typeName"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                  placeholder="Enter type name"
                  disabled={editingType?.name === 'Default'}
                />
                {editingType?.name === 'Default' && (
                  <p className="text-xs text-orange-600 mt-1">
                    The Default type name cannot be changed.
                  </p>
                )}
              </div>
              <div className="w-48">
                <label htmlFor="typeColor" className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="relative">
                  <div 
                    className="w-full h-10 rounded-md border border-gray-300 cursor-pointer"
                    style={{ backgroundColor: newTypeColor }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  ></div>
                  
                  {showColorPicker && (
                    <div className="absolute z-10 mt-1">
                      <HexColorPicker 
                        color={newTypeColor} 
                        onChange={setNewTypeColor}
                      />
                      <div className="flex mt-2 justify-end">
                        <button
                          type="button"
                          onClick={() => setShowColorPicker(false)}
                          className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4 space-x-3">
              <button
                type="button"
                onClick={() => showAddForm ? setShowAddForm(false) : setShowEditForm(false)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-2 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700"
                disabled={editingType?.name === 'Default' && newTypeName === 'Default'}
              >
                {showAddForm ? 'Add Type' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Types List */}
      {types.length > 0 ? (
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {types.map((type) => (
              <li key={type.id}>
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="flex items-center flex-1 min-w-0">
                    <div 
                      className="flex-shrink-0 w-6 h-6 rounded"
                      style={{ backgroundColor: type.color }}
                    ></div>
                    <div className="px-4 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {type.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {new Date(type.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleEditClick(type)}
                      className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {type.name !== 'Default' && (
                      <button
                        onClick={() => handleDeleteType(type.id)}
                        className="ml-2 inline-flex items-center p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No types have been created yet.</p>
        </div>
      )}
    </div>
  );
};

export default TypeSettings; 